import "./DatabaseSettingsPane.css";
import "./settings-ui.css";
import { useEffect, useState } from "preact/hooks";
import { DestructiveActionButton } from "../../components/DestructiveActionButton/DestructiveActionButton.jsx";
import { DashboardPage, createDashboardLayout } from "../../components/DashboardPage/DashboardPage.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { StatusMessage } from "../../components/StatusMessage/StatusMessage.jsx";
import { localizeErrorMessage } from "../../lib/localizeErrorMessage.js";
import {
  getDatabaseSummary,
  initializeWalletWatchDb,
  purgeWalletWatchDb
} from "../../lib/storage/walletWatchDb.js";

function SummaryField({ label, value }) {
  return (
    <div class="settings-summary-field">
      <span class="settings-summary-label">{label}</span>
      <span class="settings-summary-value">{value}</span>
    </div>
  );
}

const databasePaneLayout = createDashboardLayout({
  columns: [
    { size: "fill" },
    { size: "fill" }
  ],
  rows: [
    { size: "fill" }
  ],
  areas: [
    ["side", "stores"]
  ]
});

const databaseSideLayout = createDashboardLayout({
  columns: [
    { size: "fill" }
  ],
  rows: [
    { size: "fill" },
    { size: "fixed", value: "auto" }
  ],
  areas: [
    ["database"],
    ["purge"]
  ]
});

function footerToneClass(type) {
  if (type === "success") {
    return "tone-success";
  }

  if (type === "warning") {
    return "tone-warning";
  }

  if (type === "error") {
    return "tone-error";
  }

  return "";
}

export function DatabaseSettingsPane({ activeScope, t = (key) => key }) {
  const [summary, setSummary] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [purgeStatus, setPurgeStatus] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadSummary() {
      try {
        await initializeWalletWatchDb();
        const nextSummary = await getDatabaseSummary();

        if (!isMounted) {
          return;
        }

        setSummary(nextSummary);
        setErrorMessage("");
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(localizeErrorMessage(error, t) ?? t("messages.databaseSettings.failedToLoadDatabaseSummary"));
      }
    }

    loadSummary();

    return () => {
      isMounted = false;
    };
  }, [activeScope?.profileId, activeScope?.year, t]);

  async function handlePurgeDatabase() {
    try {
      setPurgeStatus({
        type: "pending",
        message: t("messages.databaseSettings.deletingLocalDatabase")
      });
      await purgeWalletWatchDb();
      window.location.reload();
    } catch (error) {
      setPurgeStatus({
        type: "error",
        message: localizeErrorMessage(error, t) ?? t("messages.databaseSettings.failedToDeleteLocalDatabase")
      });
    }
  }

  return (
    <DashboardPage
      layout={databasePaneLayout}
      slots={[
        {
          key: "side",
          area: "side",
          content: (
            <DashboardPage
              layout={databaseSideLayout}
              slots={[
                {
                  key: "database",
                  area: "database",
                  content: (
                    <Panel
                      padded
                      scroll
                      className="settings-database-panel"
                      header={t("database")}
                      footer={errorMessage || t("messages.databaseSettings.localDatabaseSummaryAndCurrentAppState")}
                    >
                      {summary ? (
                        <div class="settings-database-scroll settings-summary-list">
                          <SummaryField label={t("name")} value={summary.name} />
                          <SummaryField label={t("version")} value={summary.version} />
                          <SummaryField label={t("activeProfile")} value={summary.activeProfileName} />
                          <SummaryField label={t("activeYear")} value={summary.activeYear} />
                          <SummaryField label={t("language")} value={summary.language ?? t("system")} />
                        </div>
                      ) : (
                        <div class="settings-summary-empty">{t("messages.databaseSettings.loadingDatabaseSummary")}</div>
                      )}
                    </Panel>
                  )
                },
                {
                  key: "purge",
                  area: "purge",
                  fill: false,
                  content: (
                    <Panel
                      padded
                      header={t("purgeDatabase")}
                      footerClassName={footerToneClass(purgeStatus?.type)}
                      footer={purgeStatus ? (
                        <StatusMessage type={purgeStatus.type} message={purgeStatus.message} />
                      ) : (
                        t("messages.databaseSettings.deletesAllLocalProfilesBudgetsExpensesAndSettings")
                      )}
                    >
                      <div class="settings-actions">
                        <DestructiveActionButton
                          onConfirm={handlePurgeDatabase}
                        >
                          {t("deleteEntireDatabase")}
                        </DestructiveActionButton>
                      </div>
                    </Panel>
                  )
                }
              ]}
            />
          )
        },
        {
          key: "stores",
          area: "stores",
          content: (
            <Panel
              padded
              scroll
              className="settings-database-panel"
              header={t("stores")}
              footer={t("messages.databaseSettings.objectStoreNamesAndRecordCounts")}
            >
              {summary ? (
                <div class="settings-database-scroll">
                  <table class="settings-debug-table">
                  <thead>
                    <tr>
                      <th scope="col">{t("store")}</th>
                      <th scope="col">{t("records")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.stores.map((store) => (
                      <tr key={store.name}>
                        <td>{store.name}</td>
                        <td>{store.count}</td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
              ) : (
                <div class="settings-summary-empty">{t("messages.databaseSettings.loadingStoreCounts")}</div>
              )}
            </Panel>
          )
        }
      ]}
    />
  );
}
